import { useCallback, useEffect, useMemo, useState } from "react"

import { buildMetricNameIdLookups, useMetricRecords } from "@/metrics/registry"
import { useScopedStorageKey } from "@/workspaces/scope"

import { useMetricValuesBySimulationTransportQuery } from "../routes/dashboardTransportQueries"
import {
  MAX_METRIC_IDS_PER_SIMULATION_VALUES_REQUEST,
  serializeMetricValuesBySimulationRequest,
} from "../widgets/utils/transportDashboardQueries"

const DRILLDOWN_METRICS_STORAGE_KEY = "dashboard-drilldown-additional-metrics"
export const ADDITIONAL_METRIC_VALUE_LOADING_TIMEOUT_MS = 10_000

const parsePersistedAdditionalMetricIds = (stored: string) => {
  const parsed = JSON.parse(stored) as unknown

  return Array.isArray(parsed) &&
    parsed.every((metricId): metricId is string => typeof metricId === "string")
    ? parsed.slice(0, MAX_METRIC_IDS_PER_SIMULATION_VALUES_REQUEST)
    : []
}

export const useDrillDownAdditionalMetrics = ({
  simulationOutputIds,
}: {
  simulationOutputIds: string[]
}) => {
  const [additionalMetricIds, setAdditionalMetricIds] = useState<string[]>([])
  const [isMetricModalOpen, setIsMetricModalOpen] = useState(false)
  const [tempSelectedMetrics, setTempSelectedMetrics] = useState<string[]>([])

  const { metricRecords } = useMetricRecords()

  // Scoped by org + workspace so a workspace switch can't read or write
  // another workspace's drill-down metric selection.
  const storageKey = useScopedStorageKey(DRILLDOWN_METRICS_STORAGE_KEY)

  useEffect(() => {
    if (!storageKey) {
      setAdditionalMetricIds([])
      return
    }
    try {
      const stored = localStorage.getItem(storageKey)
      setAdditionalMetricIds(
        stored ? parsePersistedAdditionalMetricIds(stored) : [],
      )
    } catch {
      // Ignore localStorage errors.
    }
  }, [storageKey])

  useEffect(() => {
    if (!storageKey) {
      return
    }
    try {
      localStorage.setItem(storageKey, JSON.stringify(additionalMetricIds))
    } catch {
      // Ignore localStorage errors.
    }
  }, [additionalMetricIds, storageKey])

  const { metricNameToId, metricIdToName } = useMemo(
    () => buildMetricNameIdLookups(metricRecords),
    [metricRecords],
  )

  const additionalMetricUUIDs = useMemo(
    () =>
      additionalMetricIds
        .map((name) => metricNameToId[name])
        .filter((id): id is string => Boolean(id)),
    [additionalMetricIds, metricNameToId],
  )
  const uniqueSimulationOutputIds = useMemo(
    () => Array.from(new Set(simulationOutputIds)),
    [simulationOutputIds],
  )

  const isAdditionalMetricsQueryEnabled =
    uniqueSimulationOutputIds.length > 0 && additionalMetricUUIDs.length > 0

  const additionalMetricValuesQuery = useMetricValuesBySimulationTransportQuery(
    {
      request: serializeMetricValuesBySimulationRequest({
        simulationOutputIds: uniqueSimulationOutputIds,
        metricIds: additionalMetricUUIDs,
      }),
      options: {
        enabled: isAdditionalMetricsQueryEnabled,
      },
    },
  )

  const [hasAdditionalMetricValueLoadingTimedOut, setHasTimedOut] =
    useState(false)
  const isAdditionalMetricValuesFetchInFlight =
    additionalMetricValuesQuery.isLoading ||
    additionalMetricValuesQuery.isFetching

  const additionalMetricQueryKey = useMemo(
    () =>
      `${uniqueSimulationOutputIds.join(",")}:${additionalMetricUUIDs.join(",")}`,
    [additionalMetricUUIDs, uniqueSimulationOutputIds],
  )

  useEffect(() => {
    setHasTimedOut(false)

    if (
      !isAdditionalMetricsQueryEnabled ||
      additionalMetricValuesQuery.data?.values !== undefined ||
      !isAdditionalMetricValuesFetchInFlight
    ) {
      return
    }

    const timeoutId = window.setTimeout(() => {
      setHasTimedOut(true)
    }, ADDITIONAL_METRIC_VALUE_LOADING_TIMEOUT_MS)

    return () => window.clearTimeout(timeoutId)
  }, [
    additionalMetricQueryKey,
    additionalMetricValuesQuery.data?.values,
    isAdditionalMetricValuesFetchInFlight,
    isAdditionalMetricsQueryEnabled,
  ])

  const isAdditionalMetricValueLoading =
    isAdditionalMetricsQueryEnabled &&
    additionalMetricValuesQuery.data?.values === undefined &&
    !hasAdditionalMetricValueLoadingTimedOut &&
    isAdditionalMetricValuesFetchInFlight

  const additionalValuesMap = useMemo(() => {
    const map = new Map<string, Map<string, string | number>>()
    if (!additionalMetricValuesQuery.data?.values) {
      return map
    }

    for (const item of additionalMetricValuesQuery.data.values) {
      if (!map.has(item.simulation_output_id)) {
        map.set(item.simulation_output_id, new Map())
      }
      const metricName = metricIdToName[item.metric_id] || item.metric_id
      map.get(item.simulation_output_id)!.set(metricName, item.value)
    }
    return map
  }, [additionalMetricValuesQuery.data?.values, metricIdToName])

  const metricDisplayNames = useMemo(() => {
    const names = new Map<string, string>()

    for (const metric of Object.values(metricRecords)) {
      if (additionalMetricIds.includes(metric.metric_name)) {
        names.set(metric.metric_name, metric.metric_display_name)
      }
    }

    return names
  }, [additionalMetricIds, metricRecords])

  const handleToggleMetric = useCallback(
    (metricId: string, isSelected: boolean) => {
      setTempSelectedMetrics((prev) => {
        if (!isSelected) {
          return prev.filter((id) => id !== metricId)
        }

        if (
          prev.includes(metricId) ||
          prev.length >= MAX_METRIC_IDS_PER_SIMULATION_VALUES_REQUEST
        ) {
          return prev
        }

        return [...prev, metricId]
      })
    },
    [],
  )

  const handleOpenMetricModal = useCallback(() => {
    setTempSelectedMetrics(additionalMetricIds)
    setIsMetricModalOpen(true)
  }, [additionalMetricIds])

  const handleApplyMetrics = useCallback(() => {
    setAdditionalMetricIds(
      tempSelectedMetrics.slice(
        0,
        MAX_METRIC_IDS_PER_SIMULATION_VALUES_REQUEST,
      ),
    )
    setIsMetricModalOpen(false)
  }, [tempSelectedMetrics])

  const handleRemoveMetric = useCallback((metricId: string) => {
    setAdditionalMetricIds((prev) => prev.filter((id) => id !== metricId))
  }, [])

  return {
    additionalMetricIds,
    additionalValuesMap,
    handleApplyMetrics,
    handleOpenMetricModal,
    handleRemoveMetric,
    handleToggleMetric,
    isAdditionalMetricValueLoading,
    isMetricModalOpen,
    metricDisplayNames,
    metricRecords,
    setIsMetricModalOpen,
    tempSelectedMetrics,
  }
}
